// src/utils/driverValidation.js
import { DRIVER_STATUS, VERIFICATION_STATUS, VEHICLE_TYPE, LICENSE_TYPE } from '../store/driverSlice';

// Validation error messages
export const VALIDATION_MESSAGES = {
  REQUIRED: 'This field is required',
  INVALID_EMAIL: 'Please enter a valid email address',
  INVALID_PHONE: 'Please enter a valid phone number',
  INVALID_DATE: 'Please enter a valid date',
  INVALID_LICENSE: 'Please enter a valid license number',
  INVALID_PLATE: 'Please enter a valid plate number',
  PASSWORD_TOO_SHORT: 'Password must be at least 8 characters long',
  PASSWORDS_DONT_MATCH: 'Passwords do not match',
  INVALID_YEAR: 'Please enter a valid year',
  FUTURE_DATE: 'Date cannot be in the future',
  EXPIRED_LICENSE: 'License has expired',
  INVALID_AGE: 'Driver must be at least 18 years old'
};

// Regular expressions for validation
export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^\+?[\d\s\-\(\)]{10,}$/,
  LICENSE_NUMBER: /^[A-Z0-9]{8,15}$/,
  PLATE_NUMBER: /^[A-Z0-9\-]{3,8}$/,
  POSTAL_CODE: /^\d{5}(-\d{4})?$/,
  ACCOUNT_NUMBER: /^\d{8,17}$/,
  ROUTING_NUMBER: /^\d{9}$/
};

// Driver data structure schema
export const DRIVER_SCHEMA = {
  personalInfo: {
    firstName: { required: false, type: 'string', minLength: 2, maxLength: 50 },
    lastName: { required: false, type: 'string', minLength: 2, maxLength: 50 },
    email: { required: false, type: 'email', pattern: VALIDATION_PATTERNS.EMAIL },
    phone: { required: false, type: 'phone', pattern: VALIDATION_PATTERNS.PHONE },
    dateOfBirth: { required: false, type: 'date', minAge: 18 },
    address: { required: false, type: 'string', minLength: 10, maxLength: 200 },
    emergencyContact: {
      name: { required: false, type: 'string', minLength: 2, maxLength: 100 },
      phone: { required: false, type: 'phone', pattern: VALIDATION_PATTERNS.PHONE },
      relationship: { required: false, type: 'string', minLength: 2, maxLength: 50 }
    }
  },  licenseInfo: {
    licenseNumber: { required: false, type: 'string', pattern: VALIDATION_PATTERNS.LICENSE_NUMBER },
    licenseType: { required: false, type: 'enum', values: Object.values(LICENSE_TYPE) },
    issueDate: { required: false, type: 'date' },
    expiryDate: { required: false, type: 'date', futureDate: false },
    issuingState: { required: false, type: 'string', minLength: 2, maxLength: 2 }
  },
  vehicleInfo: {
    make: { required: false, type: 'string', minLength: 2, maxLength: 50 },
    model: { required: false, type: 'string', minLength: 2, maxLength: 50 },
    year: { required: false, type: 'number', min: 2010, max: new Date().getFullYear() + 1 },
    color: { required: false, type: 'string', minLength: 3, maxLength: 30 },
    plateNumber: { required: false, type: 'string', pattern: VALIDATION_PATTERNS.PLATE_NUMBER },
    vehicleType: { required: false, type: 'enum', values: Object.values(VEHICLE_TYPE) },
    registrationNumber: { required: false, type: 'string', minLength: 5, maxLength: 20 },
    insurancePolicyNumber: { required: false, type: 'string', minLength: 5, maxLength: 30 }
  },
  bankingInfo: {
    accountHolderName: { required: false, type: 'string', minLength: 2, maxLength: 100 },
    accountNumber: { required: false, type: 'string', pattern: VALIDATION_PATTERNS.ACCOUNT_NUMBER },
    routingNumber: { required: false, type: 'string', pattern: VALIDATION_PATTERNS.ROUTING_NUMBER },
    bankName: { required: false, type: 'string', minLength: 2, maxLength: 100 }
  },
  preferences: {
    maxDistance: { required: false, type: 'number', min: 5, max: 100 },
    workingHours: {
      start: { required: false, type: 'time' },
      end: { required: false, type: 'time' }
    },
    acceptSharedRides: { required: false, type: 'boolean' },
    acceptLongDistance: { required: false, type: 'boolean' }
  }
};

// Validation functions
export const validateField = (value, fieldSchema, fieldName) => {
  const errors = [];

  // Required field validation
  if (fieldSchema.required && (!value || value.toString().trim() === '')) {
    errors.push(VALIDATION_MESSAGES.REQUIRED);
    return errors;
  }

  // Skip further validation if field is not required and empty
  if (!fieldSchema.required && (!value || value.toString().trim() === '')) {
    return errors;
  }

  // Type-specific validation
  switch (fieldSchema.type) {
    case 'email':
      if (!VALIDATION_PATTERNS.EMAIL.test(value)) {
        errors.push(VALIDATION_MESSAGES.INVALID_EMAIL);
      }
      break;

    case 'phone':
      if (!VALIDATION_PATTERNS.PHONE.test(value)) {
        errors.push(VALIDATION_MESSAGES.INVALID_PHONE);
      }
      break;

    case 'date':
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        errors.push(VALIDATION_MESSAGES.INVALID_DATE);
      } else {
        // Check minimum age for date of birth
        if (fieldSchema.minAge) {
          const age = (new Date() - date) / (365.25 * 24 * 60 * 60 * 1000);
          if (age < fieldSchema.minAge) {
            errors.push(VALIDATION_MESSAGES.INVALID_AGE);
          }
        }
        
        // Check if date should be in the future
        if (fieldSchema.futureDate && date <= new Date()) {
          errors.push(VALIDATION_MESSAGES.EXPIRED_LICENSE);
        }
      }
      break;

    case 'number':
      const num = Number(value);
      if (isNaN(num)) {
        errors.push('Please enter a valid number');
      } else {
        if (fieldSchema.min !== undefined && num < fieldSchema.min) {
          errors.push(`Value must be at least ${fieldSchema.min}`);
        }
        if (fieldSchema.max !== undefined && num > fieldSchema.max) {
          errors.push(`Value must be at most ${fieldSchema.max}`);
        }
      }
      break;

    case 'enum':
      if (!fieldSchema.values.includes(value)) {
        errors.push(`Please select a valid option`);
      }
      break;

    case 'string':
      if (fieldSchema.minLength && value.length < fieldSchema.minLength) {
        errors.push(`Must be at least ${fieldSchema.minLength} characters long`);
      }
      if (fieldSchema.maxLength && value.length > fieldSchema.maxLength) {
        errors.push(`Must be at most ${fieldSchema.maxLength} characters long`);
      }
      break;
  }

  // Pattern validation
  if (fieldSchema.pattern && !fieldSchema.pattern.test(value)) {
    switch (fieldName) {
      case 'licenseNumber':
        errors.push(VALIDATION_MESSAGES.INVALID_LICENSE);
        break;
      case 'plateNumber':
        errors.push(VALIDATION_MESSAGES.INVALID_PLATE);
        break;
      default:
        errors.push('Please enter a valid format');
    }
  }

  return errors;
};

// Validate nested object
export const validateObject = (obj, schema, parentKey = '') => {
  const errors = {};

  for (const [key, fieldSchema] of Object.entries(schema)) {
    const fullKey = parentKey ? `${parentKey}.${key}` : key;
    const value = obj?.[key];

    if (typeof fieldSchema === 'object' && !fieldSchema.type && !fieldSchema.required) {
      // Nested object
      const nestedErrors = validateObject(value, fieldSchema, fullKey);
      if (Object.keys(nestedErrors).length > 0) {
        errors[key] = nestedErrors;
      }
    } else {
      // Field validation
      const fieldErrors = validateField(value, fieldSchema, key);
      if (fieldErrors.length > 0) {
        errors[key] = fieldErrors;
      }
    }
  }

  return errors;
};

// Main driver validation function
export const validateDriverData = (driverData) => {
  return validateObject(driverData, DRIVER_SCHEMA);
};

// Validate specific sections
export const validatePersonalInfo = (personalInfo) => {
  return validateObject(personalInfo, DRIVER_SCHEMA.personalInfo);
};

export const validateLicenseInfo = (licenseInfo) => {
  return validateObject(licenseInfo, DRIVER_SCHEMA.licenseInfo);
};

export const validateVehicleInfo = (vehicleInfo) => {
  return validateObject(vehicleInfo, DRIVER_SCHEMA.vehicleInfo);
};

export const validateBankingInfo = (bankingInfo) => {
  return validateObject(bankingInfo, DRIVER_SCHEMA.bankingInfo);
};

// Helper functions
export const hasErrors = (errors) => {
  if (typeof errors !== 'object' || errors === null) return false;
  
  for (const value of Object.values(errors)) {
    if (Array.isArray(value) && value.length > 0) return true;
    if (typeof value === 'object' && hasErrors(value)) return true;
  }
  
  return false;
};

export const getFirstError = (errors) => {
  if (typeof errors !== 'object' || errors === null) return null;
  
  for (const value of Object.values(errors)) {
    if (Array.isArray(value) && value.length > 0) return value[0];
    if (typeof value === 'object') {
      const nestedError = getFirstError(value);
      if (nestedError) return nestedError;
    }
  }
  
  return null;
};

// Status validation helpers
export const isValidDriverStatus = (status) => {
  return Object.values(DRIVER_STATUS).includes(status);
};

export const isValidVerificationStatus = (status) => {
  return Object.values(VERIFICATION_STATUS).includes(status);
};

export const canTransitionStatus = (fromStatus, toStatus) => {
  const validTransitions = {
    [DRIVER_STATUS.PENDING]: [DRIVER_STATUS.APPROVED, DRIVER_STATUS.REJECTED],
    [DRIVER_STATUS.APPROVED]: [DRIVER_STATUS.ACTIVE, DRIVER_STATUS.SUSPENDED],
    [DRIVER_STATUS.ACTIVE]: [DRIVER_STATUS.INACTIVE, DRIVER_STATUS.SUSPENDED],
    [DRIVER_STATUS.INACTIVE]: [DRIVER_STATUS.ACTIVE, DRIVER_STATUS.SUSPENDED],
    [DRIVER_STATUS.SUSPENDED]: [DRIVER_STATUS.ACTIVE, DRIVER_STATUS.INACTIVE],
    [DRIVER_STATUS.REJECTED]: [] // No transitions from rejected
  };

  return validTransitions[fromStatus]?.includes(toStatus) || false;
};
